/**
 * Tracks live HpStripeBanner instances keyed by their root element.
 * Used instead of `dataset.height` to survive element cloning and
 * to allow proper re-initialization after a banner is removed and re-added.
 */
const instanceMap = new WeakMap<HTMLElement, HpStripeBanner>();

export class HpStripeBanner {
	root: HTMLElement;
	media: HTMLElement;

	private mediaContainerClass: string;
	private resizeObserver: ResizeObserver;
	private mutationObserver: MutationObserver | null = null;
	private onMediaLoad: (() => void) | null = null;
	private onMediaError: (() => void) | null = null;
	private destroyed = false;

	constructor(root: HTMLElement, mediaContainerClass = '.banner-media') {
		this.root = root;
		this.mediaContainerClass = mediaContainerClass;

		// Set data-height for backward compatibility (external code may check it)
		this.root.dataset.height = '';

		this.media = this.queryMedia();

		// Observe size changes on the root, the media, and the viewport
		this.resizeObserver = new ResizeObserver(this.update);
		this.resizeObserver.observe(this.root);
		this.resizeObserver.observe(this.media);
		this.resizeObserver.observe(document.documentElement);

		// Watch for replacement of the inner media element (e.g. by a framework)
		this.setupMutationObserver();

		// React to async media loading (images, videos)
		this.setupMediaLoadListeners();

		// Compute initial height
		this.update();
	}

	// ---------------------------------------------------------------------------
	// Public API
	// ---------------------------------------------------------------------------

	/**
	 * Recompute the banner height from the current media element.
	 * Called automatically by ResizeObserver; can also be called manually.
	 */
	update = (): void => {
		if (this.destroyed) return;
		this.doUpdate();
	};

	/**
	 * Tear down all observers and listeners. After calling this the instance
	 * is unusable — create a new one if the banner reappears.
	 */
	destroy(): void {
		if (this.destroyed) return;
		this.destroyed = true;

		this.resizeObserver.disconnect();

		if (this.mutationObserver) {
			this.mutationObserver.disconnect();
			this.mutationObserver = null;
		}

		this.removeMediaLoadListeners();

		instanceMap.delete(this.root);

		// Clean up the data attribute so a future clone / re-add is detected as fresh
		delete this.root.dataset.height;
	}

	// ---------------------------------------------------------------------------
	// Internal helpers
	// ---------------------------------------------------------------------------

	/** Find the media element inside the banner using the configured selector. */
	private queryMedia(): HTMLElement {
		const mc = this.mediaContainerClass;
		return this.root.querySelector<HTMLElement>(
			`${mc} img, ${mc} picture, ${mc} video`
		)
			|| this.root.querySelector<HTMLElement>(mc)
			|| this.root;
	}

	/**
	 * For <picture> elements we must listen on the inner <img> because
	 * load/error events do not bubble from <img> through <picture>.
	 */
	private getMediaEventTarget(): HTMLElement {
		if (this.media.tagName === 'PICTURE') {
			return this.media.querySelector<HTMLElement>('img') || this.media;
		}
		return this.media;
	}

	/** Observe the media container so we notice when the inner element is swapped. */
	private setupMutationObserver(): void {
		const container =
			this.root.querySelector(this.mediaContainerClass) || this.root;
		this.mutationObserver = new MutationObserver(this.onMediaMutation);
		this.mutationObserver.observe(container, {
			childList: true,
			subtree: true,
		});
	}

	/** Fires when children of the media container change. */
	private onMediaMutation = (): void => {
		if (this.destroyed) return;

		// Re-run the query — if the best match changed, refresh
		const fresh = this.queryMedia();
		if (fresh !== this.media || !this.media.isConnected) {
			this.refreshMedia(fresh);
		}
	};

	/**
	 * Returns true when `this.media` is still a valid descendant of `this.root`.
	 * Used as a belt-and-suspenders check in `doUpdate`.
	 */
	private isMediaValid(): boolean {
		return this.root.contains(this.media);
	}

	/** Swap out the tracked media element and re-wire observers/listeners. */
	private refreshMedia(newMedia?: HTMLElement): void {
		// Detach from old media
		this.resizeObserver.unobserve(this.media);
		this.removeMediaLoadListeners();

		// Pick up the new media
		this.media = newMedia ?? this.queryMedia();

		// Attach to new media
		this.resizeObserver.observe(this.media);
		this.setupMediaLoadListeners();

		// The container may have changed too (e.g. .banner-media was added later)
		if (this.mutationObserver) {
			this.mutationObserver.disconnect();
			this.setupMutationObserver();
		}

		this.doUpdate();
	}

	private setupMediaLoadListeners(): void {
		const target = this.getMediaEventTarget();

		this.onMediaLoad = () => this.doUpdate();
		this.onMediaError = () => this.doUpdate();

		target.addEventListener('load', this.onMediaLoad);
		target.addEventListener('error', this.onMediaError);

		if (target.tagName === 'VIDEO') {
			target.addEventListener('loadedmetadata', this.onMediaLoad);
		}
	}

	private removeMediaLoadListeners(): void {
		if (!this.onMediaLoad || !this.onMediaError) return;

		const loadHandler = this.onMediaLoad;
		const errorHandler = this.onMediaError;
		const target = this.getMediaEventTarget();

		target.removeEventListener('load', loadHandler);
		target.removeEventListener('error', errorHandler);

		if (target.tagName === 'VIDEO') {
			target.removeEventListener('loadedmetadata', loadHandler);
		}

		this.onMediaLoad = null;
		this.onMediaError = null;
	}

	/** Core update — reads media height and writes the CSS custom property. */
	private doUpdate = (): void => {
		if (this.destroyed) return;

		// Self-destruct if the root was removed from the document.
		// The MutationObserver in runtime.ts will call init() again
		// if the banner is re-added later.
		if (!this.root.isConnected) {
			this.destroy();
			return;
		}

		// If the media element was torn out of the DOM try to find a new one.
		if (!this.media.isConnected || !this.isMediaValid()) {
			this.refreshMedia();
			return;
		}

		// On stripe-narrow-1 / stripe-narrow-2 / stripe-narrow-3 below the 1280 px breakpoint
		// the CSS sizes the inner <img>/<video> via `height: var(--banner-height)`.
		// Writing the JS-measured offsetHeight back onto that same custom property
		// creates a circular lock: if the media hasn't loaded yet and offsetHeight
		// is 0, the inline `--banner-height: 0px` overrides the CSS default
		// (12rem) and the element can never render at a non-zero height.
		//
		// For these variants on mobile we actively *remove* the inline property
		// so the CSS default value wins.  At ≥1280 px the CSS switches to
		// `height: 100 %` (no longer depends on `--banner-height`) so the
		// normal path is safe.
		const isHeightMatchedBanner =
			this.root.classList.contains('stripe-narrow-1') ||
			this.root.classList.contains('stripe-narrow-2') ||
			this.root.classList.contains('stripe-narrow-3');
		if (
			isHeightMatchedBanner &&
			window.matchMedia('(max-width: 1279px)').matches
		) {
			this.root.style.removeProperty('--banner-height');
			delete this.root.dataset.height;
			return;
		}

		const height = this.media.offsetHeight;
		this.root.dataset.height = `${height}px`;
		this.root.style.setProperty('--banner-height', `${height}px`);
	};
}

// ---------------------------------------------------------------------------
// Module-level init
// ---------------------------------------------------------------------------

export const init = () => {
	const newInstances: HpStripeBanner[] = [];

	const banners = Array.from(
		document.querySelectorAll<HTMLElement>('.hp-stripe-banner')
	);
	for (const root of banners) {
		if (instanceMap.has(root)) continue; // already tracked

		const instance = new HpStripeBanner(root, '.banner-media');
		instanceMap.set(root, instance);
		newInstances.push(instance);
	}

	const pictureBlocks = Array.from(
		document.querySelectorAll<HTMLElement>('.std-picture-block.stripe-mask')
	);
	for (const root of pictureBlocks) {
		if (instanceMap.has(root)) continue;

		const instance = new HpStripeBanner(root, '.block-media');
		instanceMap.set(root, instance);
		newInstances.push(instance);
	}

	return newInstances;
};

export default {
	init,
};
